import { useEffect, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Mail, Clock, Globe, Check, Send } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import HelpBubble from "../components/HelpBubble";

export default function Digest() {
  const { user } = useAuth();
  const [cfg, setCfg] = useState({ enabled: false, hour: 8, timezone: "America/New_York", email_override: "" });
  const [tzs, setTzs] = useState([]);
  const [last, setLast] = useState({ last_sent_at: null, last_result: null });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/digest/settings");
      setCfg({
        enabled: data.enabled,
        hour: data.hour,
        timezone: data.timezone,
        email_override: data.email_override || "",
      });
      setTzs(data.common_timezones || []);
      setLast({ last_sent_at: data.last_sent_at, last_result: data.last_result });
    } catch (err) {
      console.error("digest config load failed", err);
      toast.error(err?.response?.data?.detail || "Failed to load digest config");
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const save = async () => {
    setBusy(true);
    try {
      await api.post("/digest/settings", cfg);
      toast.success(cfg.enabled ? `Digest armed · daily at ${cfg.hour}:00 ${cfg.timezone}` : "Digest disabled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setBusy(false); }
  };

  const sendTest = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/digest/send-test");
      if (data.sent) {
        toast.success(`Test sent to ${cfg.email_override || user?.email}`);
      } else {
        const err = data.error || "";
        const hint = /testing emails to your own|verify a domain/i.test(err)
          ? "On the Resend free plan the recipient must match your Resend account email — or verify a domain at resend.com/domains."
          : err || "Send failed — check your Resend key on Team page";
        toast.error(hint);
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    } finally { setBusy(false); }
  };

  const hours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div className="p-6 max-w-[1100px]" data-testid="digest-page" style={{ fontFamily: "Manrope" }}>
      <div className="mb-4">
        <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">Daily Digest <HelpBubble feature="digest_arm" /></h1>
        <p className="text-xs text-slate-500 mt-1 font-mono">
          One email per morning summarizing yesterday's profit, overnight Auto-Operator activity, exclusion applies, and anything that needs your attention.
        </p>
      </div>

      <div className={`mb-4 rounded-md border p-4 flex items-start gap-3 ${cfg.enabled ? "bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-200" : "bg-white border-slate-200"}`}>
        <div className={`w-10 h-10 rounded-md flex items-center justify-center ${cfg.enabled ? "bg-blue-600 text-white" : "bg-slate-100 text-slate-500"}`}>
          <Mail className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="text-sm font-semibold text-slate-900 flex items-center gap-2">
            Daily Digest
            <span className={`text-[10px] font-mono px-2 py-0.5 rounded ${cfg.enabled ? "bg-blue-600 text-white" : "bg-slate-200 text-slate-600"}`}>
              {cfg.enabled ? "ARMED" : "OFF"}
            </span>
          </div>
          <div className="text-[11px] text-slate-500 font-mono">
            {cfg.enabled
              ? `Ships every day at ${String(cfg.hour).padStart(2,"0")}:00 ${cfg.timezone} to ${cfg.email_override || user?.email}`
              : "Pick a delivery time and arm the digest below."}
          </div>
          {last.last_sent_at && (
            <div className="text-[10px] text-slate-500 font-mono mt-0.5">
              Last sent {new Date(last.last_sent_at).toLocaleString()} · {last.last_result?.sent ? "delivered" : "failed"}
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white border border-slate-200 rounded-md p-5 space-y-3" data-testid="digest-settings">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono">Delivery settings</div>

          <label className="flex items-center gap-2 text-sm text-slate-900">
            <input
              type="checkbox"
              checked={cfg.enabled}
              onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
              data-testid="digest-enabled"
            />
            Enable daily digest
          </label>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[10px] text-slate-500 font-mono flex items-center gap-1"><Clock className="w-3 h-3" /> Hour</label>
              <select
                value={cfg.hour}
                onChange={(e) => setCfg({ ...cfg, hour: parseInt(e.target.value) })}
                data-testid="digest-hour"
                className="w-full mt-1 bg-white border border-slate-200 rounded px-2 py-1.5 text-xs font-mono"
              >
                {hours.map((h) => (
                  <option key={h} value={h}>{String(h).padStart(2,"0")}:00</option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] text-slate-500 font-mono flex items-center gap-1"><Globe className="w-3 h-3" /> Timezone</label>
              <select
                value={cfg.timezone}
                onChange={(e) => setCfg({ ...cfg, timezone: e.target.value })}
                data-testid="digest-timezone"
                className="w-full mt-1 bg-white border border-slate-200 rounded px-2 py-1.5 text-xs font-mono"
              >
                {tzs.map((tz) => <option key={tz} value={tz}>{tz}</option>)}
              </select>
            </div>
          </div>

          <div>
            <label className="text-[10px] text-slate-500 font-mono">Override email (optional)</label>
            <input
              value={cfg.email_override}
              onChange={(e) => setCfg({ ...cfg, email_override: e.target.value })}
              placeholder={user?.email}
              data-testid="digest-email"
              className="w-full mt-1 bg-white border border-slate-200 rounded px-2 py-1.5 text-xs font-mono"
            />
            <p className="text-[10px] text-slate-500 font-mono mt-1">Leave blank to use your account email.</p>
          </div>

          <div className="pt-2 flex items-center gap-2">
            <button
              onClick={save}
              disabled={busy}
              data-testid="digest-save-btn"
              className="flex-1 bg-blue-600 text-white hover:bg-blue-700 rounded-md px-3 py-2 text-sm font-medium disabled:opacity-60"
            >
              {busy ? "Saving…" : "Save settings"}
            </button>
            <button
              onClick={sendTest}
              disabled={busy}
              data-testid="digest-test-btn"
              className="bg-white border border-slate-200 hover:border-blue-400 text-slate-900 rounded-md px-3 py-2 text-sm font-medium disabled:opacity-60 flex items-center gap-2"
            >
              <Send className="w-4 h-4" /> Send test now
            </button>
          </div>

          <p className="text-[10px] text-slate-500 font-mono leading-relaxed pt-2 border-t border-slate-200">
            Requires a Resend API key in workspace settings. Without it the test will fail with a clear error. Go to Team & Keys to configure.
          </p>
        </div>

        <div className="bg-white border border-slate-200 rounded-md p-5" data-testid="digest-preview">
          <div className="text-[11px] uppercase tracking-wider text-slate-500 font-mono mb-3">What's in the email</div>
          <div className="space-y-2.5 text-sm text-slate-700">
            {[
              ["Yesterday's totals", "Spend · revenue · net profit · ROI"],
              ["Overnight automation", "Auto-Operator rotations, winners scaled, exclusions applied"],
              ["Top winners", "5 best campaigns ranked by net profit"],
              ["Top losers", "5 worst campaigns — which to pause"],
              ["Vertical heatmap", "Scale / Watch / Kill verdict per vertical"],
              ["Attention needed", "ACTIVE campaigns at ROI < -10% with spend > $50"],
            ].map(([title, sub]) => (
              <div key={title} className="flex items-start gap-2">
                <Check className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
                <div>
                  <div className="text-sm text-slate-900 font-medium">{title}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{sub}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
